import { GeometryType } from '../../types/geometry';
import {
  VIEW_MODE,
  NOTE_VIEW_MODE,
  type ViewMode,
  type NoteViewMode,
  type Coordinates,
} from '../../types';
import type { DateRange } from '../../pages/DashboardPage/components/DateRangeSelector';

export type { NoteViewMode };

import {
  PANEL_DEFAULT_WIDTH,
  PANEL_WIDTH_KEY,
  SWIPE_DOWN_HINT_SEEN_KEY,
  SWIPE_UP_HINT_SEEN_KEY,
} from '../../constants/ui';
// Aliased to avoid shadowing the native `localStorage` global still used for
// the legacy panel-width read below.
import { localStorage as storage } from '../../utils/helpers';
import { getLayoutMode } from '../../utils/deviceDetection';
import {
  closeCompetingArtifacts,
  withShareFormGuard,
} from '../../artifacts/panelHelpers';
import type { FormConflictSlice } from './formConflictSlice';

// ===== CONTEXT MENU TYPES =====
// Unified context menu state for all menu types (single source of truth)

/** Context menu type discriminator */
export type ContextMenuType = 'geometry' | 'task' | 'map';

/** Geometry-specific context menu data (Edit/Delete geometry) */
export interface GeometryContextMenuData {
  geometryId: string;
  geometryType: GeometryType;
}

/** Task-specific context menu data (Show details, Edit task, Delete task, Create here) */
export interface TaskContextMenuData {
  taskId: string;
  geometryId: string;
  coordinates: Coordinates;
}

/** Map-specific context menu data (Create task here) */
export interface MapContextMenuData {
  coordinates: Coordinates;
}

/** Discriminator for which artifact owns the on-map hover preview. */
export type HoveredMarkerKind = 'task' | 'note' | 'obstruction';

/**
 * Hovered marker preview state — drives the unified on-map hover card for
 * task markers, task line/shape geometries, and note markers. The discriminator
 * (`kind`) selects which info card the renderer mounts inside the popup.
 */
export interface HoveredMarkerPreview {
  kind: HoveredMarkerKind;
  /** Task id (when kind = 'task') or Note id (when kind = 'note'). */
  id: string;
  coordinates: Coordinates;
}

/**
 * Hovered task line/shape geometry preview. Strukturalnie identyczny z
 * `HoveredMarkerPreview` — alias daje semantyczne nazewnictwo dla writera
 * z `LineRenderer`/`ShapeRenderer` i własny slot w stanie, eliminując
 * wyścig między marker hoverem a geometry hoverem.
 */
export type HoveredGeometryPreview = HoveredMarkerPreview;

function areHoverPreviewsEqual(
  a: HoveredMarkerPreview | null,
  b: HoveredMarkerPreview | null
): boolean {
  if (a === null || b === null) return a === b;
  return (
    a.kind === b.kind &&
    a.id === b.id &&
    a.coordinates.lat === b.coordinates.lat &&
    a.coordinates.lng === b.coordinates.lng
  );
}

/** Unified context menu state */
export interface ContextMenuState {
  isVisible: boolean;
  type: ContextMenuType | null;
  position: { x: number; y: number };
  geometryData: GeometryContextMenuData | null;
  taskData: TaskContextMenuData | null;
  mapData: MapContextMenuData | null;
}

// ===== TYPES FOR CROSS-SLICE ACCESS =====
//
// `closeAll` reaches into tasks/notes/formConflict to fully reset cross-cutting
// state (panel flags now live on the artifact slices since R1).
interface StoreWithCrossSlice {
  ui: UiSlice['ui'];
  tasks: {
    selectedId: string | null;
    detailsIsOpen: boolean;
    formIsOpen: boolean;
    formMode: 'create' | 'edit' | null;
    formAnchorPoint: Coordinates | null;
    closeDetails: () => void;
    closeForm: () => void;
    selectTask: (id: string | null) => void;
  };
  notes: {
    detailsIsOpen: boolean;
    formIsOpen: boolean;
    closeDetails: () => void;
    closeForm: () => void;
  };
  obstructions: {
    closeDetails: () => void;
    closeForm: () => void;
  };
  shareForm: {
    formIsOpen: boolean;
    closeForm: () => void;
  };
  formConflict: FormConflictSlice['formConflict'];
  setHideOtherMarkers?: (value: boolean) => void;
  closeAllToolPanels?: () => void;
}

/** Set hideOtherMarkers to a target value via drawingToolsSlice direct setter. */
function setHideOtherMarkers(get: () => unknown, shouldHide: boolean): void {
  const state = get() as {
    setHideOtherMarkers?: (value: boolean) => void;
  };
  state.setHideOtherMarkers?.(shouldHide);
}

// ===== SLICE INTERFACE =====

export interface UiSlice {
  ui: {
    sidebar: {
      isOpen: boolean;
    };
    sidePanel: {
      isOpen: boolean;
    };
    geometriesList: {
      isOpen: boolean;
    };
    relatedTasksList: {
      isOpen: boolean;
    };
    slidePanel: {
      isMinimized: boolean; // Mobile panel minimized state
      previewMode: boolean; // Mobile TaskDetails partial expansion (shows basic info + map)
      wasMinimizedBeforeGeometriesList: boolean; // Track if panel was minimized before opening geometries list
      // swipe*HintShown: localStorage-backed (so the anon share viewer keeps
      // the hint dismissed); also server-synced for authed users.
      swipeDownHintShown: boolean;
      swipeUpHintShown: boolean;
      isFullscreen: boolean; // Desktop SlidePanel fullscreen — shared across panels (one open at a time). SlidePanel resets this to its `defaultFullscreen` on every open transition.
    };
    toolbarGuide: {
      isOpen: boolean;
      hasSeenGuide: boolean; // Persisted server-side via UserSettings (settingsBridge); not localStorage
      openedManually: boolean; // True when opened via help icon (no "Don't show again" checkbox)
    };
    mapControls: {
      isLayersExpanded: boolean; // Layer panel visibility
    };
    sharePanel: {
      isOpen: boolean; // Share manager panel visibility (right-hand SlidePanel)
    };
    toolbarScrollHint: {
      hasSeenScrollHint: boolean; // Persisted server-side via UserSettings (settingsBridge); not localStorage
    };
    // Track if any form panel is currently being resized (disables tooltip transition for real-time tracking)
    isAnyPanelResizing: boolean;
    // Current panel width (reactive during resize, unified across all SlidePanels)
    panelWidth: number;
    // Unified context menu state (single source of truth for all menu types)
    contextMenu: ContextMenuState;
    /** Timestamp until which map interactions are suppressed (after panel click-outside close) */
    interactionSuppressedUntil: number | null;
    /** Current task view mode (map, list, kanban). Persisted to localStorage. */
    taskViewMode: ViewMode;
    /** Map overlay state for task creation from list/kanban views */
    mapOverlay: {
      isOpen: boolean;
    };
    /** Dashboard v2 state */
    dashboard: {
      dateRange: DateRange;
    };
    /** Current note view mode (map or list). Persisted to localStorage. */
    noteViewMode: NoteViewMode;
    /** Current obstruction view mode (map, list, kanban). Persisted to localStorage. */
    obstructionViewMode: ViewMode;
    /** Whether the user is selecting a task location on the map (from SidePanel "Add task" button) */
    isSelectingTaskLocation: boolean;
    /** Whether the user is selecting a note location on the map (from SidePanel "Add note" button) */
    isSelectingNoteLocation: boolean;
    /** Whether the user is selecting an obstruction location on the map (from SidePanel "Add obstruction" button) */
    isSelectingObstructionLocation: boolean;
    /** Layer configurator panel state (add/edit custom layer) */
    layerConfigurator: {
      isOpen: boolean;
      editingLayerId: string | null;
    };
    /** Count of open SlidePanels (generic toolbar repositioning) */
    openSlidePanelCount: number;
    /** Marker (task pin or note pin) currently hovered on the map. Pisane
     *  wyłącznie przez `useMapMarkerHoverPreview`. Konsumowane przez
     *  `MarkerHoverPreview` z priorytetem nad `hoveredGeometryPreview`. */
    hoveredMarkerPreview: HoveredMarkerPreview | null;
    /** Artifact line/shape currently hovered on the map. Pisane wyłącznie
     *  przez `useArtifactGeometryHoverPreview` (z `LineRenderer`/`ShapeRenderer`).
     *  Konsumowane jako fallback gdy `hoveredMarkerPreview` jest null. */
    hoveredGeometryPreview: HoveredGeometryPreview | null;

    toggleSidebar: (open?: boolean) => void;
    toggleSidePanel: (open?: boolean) => void;
    toggleGeometriesList: (open?: boolean) => void;
    toggleRelatedTasksList: (open?: boolean) => void;
    setMobilePanelMinimized: (isMinimized: boolean) => void;
    setSlidePanelFullscreen: (isFullscreen: boolean) => void;
    setPreviewMode: (isPreview: boolean) => void;
    dismissSwipeDownHint: () => void;
    dismissSwipeUpHint: () => void;
    toggleToolbarGuide: (open?: boolean) => void;
    openToolbarGuideManually: () => void;
    markGuideAsSeen: () => void;
    toggleLayersPanel: () => void;
    closeLayersPanel: () => void;
    openSharePanel: () => void;
    closeSharePanel: () => void;
    toggleSharePanel: () => void;
    dismissToolbarScrollHint: () => void;
    setPanelWidth: (width: number) => void;
    setIsAnyPanelResizing: (isResizing: boolean) => void;
    closeAll: () => void;
    // Context menu actions (unified for all menu types)
    showContextMenu: (
      type: ContextMenuType,
      position: { x: number; y: number },
      data: GeometryContextMenuData | TaskContextMenuData | MapContextMenuData
    ) => void;
    hideContextMenu: () => void;
    /** Suppress map interactions for a duration (used after panel click-outside close) */
    suppressInteraction: (durationMs?: number) => void;
    setTaskViewMode: (mode: ViewMode) => void;
    openMapOverlay: () => void;
    closeMapOverlay: () => void;
    setDashboardDateRange: (range: DateRange) => void;
    setNoteViewMode: (mode: NoteViewMode) => void;
    setObstructionViewMode: (mode: ViewMode) => void;
    startTaskLocationSelection: () => void;
    cancelTaskLocationSelection: () => void;
    startNoteLocationSelection: () => void;
    cancelNoteLocationSelection: () => void;
    startObstructionLocationSelection: () => void;
    cancelObstructionLocationSelection: () => void;
    openLayerConfigurator: () => void;
    openLayerConfiguratorForEdit: (layerId: string) => void;
    closeLayerConfigurator: () => void;
    registerSlidePanel: () => void;
    unregisterSlidePanel: () => void;
    /** Set (or clear) the on-map hover preview for a task / note marker. */
    setHoveredMarkerPreview: (preview: HoveredMarkerPreview | null) => void;
    /** Set (or clear) the on-map hover preview for a task line/shape geometry. */
    setHoveredGeometryPreview: (preview: HoveredGeometryPreview | null) => void;
  };
}

// Shared by `closeMapOverlay`, the `cancel*LocationSelection` actions, and every
// `start*LocationSelection` action — drops the user out of ANY "placing a new
// artifact" mode. Clearing all three flags here keeps the task/note/obstruction
// selection modes mutually exclusive (arming one cancels the others).
function resetPlacingFlags(state: StoreWithCrossSlice): void {
  state.ui.mapOverlay.isOpen = false;
  state.ui.isSelectingTaskLocation = false;
  state.ui.isSelectingNoteLocation = false;
  state.ui.isSelectingObstructionLocation = false;
}

// ===== SLICE CREATOR =====
// Phase 2: Full implementation with Immer v10

export const createUiSlice = (
  set: (fn: (state: StoreWithCrossSlice) => void) => void,
  get: () => unknown,
  _store: unknown
) => ({
  ui: {
    sidebar: {
      isOpen: false,
    },
    sidePanel: {
      isOpen: false,
    },
    geometriesList: {
      isOpen: false,
    },
    relatedTasksList: {
      isOpen: false,
    },
    slidePanel: {
      isMinimized: false, // Mobile panel starts expanded
      previewMode: false, // Mobile TaskDetails starts in full view (set to true when opening)
      wasMinimizedBeforeGeometriesList: false, // Track if panel was minimized before opening geometries list
      // Restored from localStorage (see interface note above).
      swipeDownHintShown: storage.get(SWIPE_DOWN_HINT_SEEN_KEY, false),
      swipeUpHintShown: storage.get(SWIPE_UP_HINT_SEEN_KEY, false),
      isFullscreen: true, // Initial value; SlidePanel resets this to its `defaultFullscreen` on every open transition.
    },
    toolbarGuide: {
      isOpen: false,
      hasSeenGuide: false, // Persisted server-side via UserSettings (settingsBridge)
      openedManually: false, // Will be set to true when opened via help icon
    },
    mapControls: {
      isLayersExpanded: false, // Layer panel initially closed
    },
    sharePanel: {
      isOpen: false, // Share manager panel initially closed
    },
    toolbarScrollHint: {
      hasSeenScrollHint: false, // Persisted server-side via UserSettings (settingsBridge)
    },
    isAnyPanelResizing: false, // Tracks if any panel is currently being resized (disables tooltip transition)
    // Unified context menu state (single source of truth)
    contextMenu: {
      isVisible: false,
      type: null,
      position: { x: 0, y: 0 },
      geometryData: null,
      taskData: null,
      mapData: null,
    },
    // Interaction suppression (for click-outside panel close)
    interactionSuppressedUntil: null,
    taskViewMode: VIEW_MODE.MAP,
    mapOverlay: {
      isOpen: false,
    },
    dashboard: {
      dateRange: '30d' as DateRange,
    },
    noteViewMode: NOTE_VIEW_MODE.MAP,
    obstructionViewMode: VIEW_MODE.MAP,
    isSelectingTaskLocation: false,
    isSelectingNoteLocation: false,
    isSelectingObstructionLocation: false,
    layerConfigurator: {
      isOpen: false,
      editingLayerId: null,
    },
    openSlidePanelCount: 0,
    hoveredMarkerPreview: null as HoveredMarkerPreview | null,
    hoveredGeometryPreview: null as HoveredGeometryPreview | null,
    panelWidth: (() => {
      const stored = localStorage.getItem(PANEL_WIDTH_KEY);
      if (stored) {
        const parsed = parseInt(stored, 10);
        if (!isNaN(parsed)) return parsed;
      }
      return PANEL_DEFAULT_WIDTH;
    })(),

    toggleSidebar: (open?: boolean) => {
      set((state) => {
        state.ui.sidebar.isOpen =
          open !== undefined ? open : !state.ui.sidebar.isOpen;
      });
    },

    toggleSidePanel: (open?: boolean) => {
      set((state) => {
        const target = open !== undefined ? open : !state.ui.sidePanel.isOpen;
        if (state.ui.sidePanel.isOpen !== target) {
          state.ui.sidePanel.isOpen = target;
        }
      });
    },

    toggleGeometriesList: (open?: boolean) => {
      const isMobileLayout = getLayoutMode() === 'mobile';
      set((state) => {
        const willOpen =
          open === true ||
          (open === undefined && !state.ui.geometriesList.isOpen);
        const willClose =
          open === false ||
          (open === undefined && state.ui.geometriesList.isOpen);

        // MOBILE OPENING: Save minimized state and expand form
        if (isMobileLayout && willOpen && state.ui.slidePanel.isMinimized) {
          state.ui.slidePanel.wasMinimizedBeforeGeometriesList = true;
          state.ui.slidePanel.isMinimized = false;
        }

        // MOBILE CLOSING: Restore minimization if was minimized before
        if (
          isMobileLayout &&
          willClose &&
          state.ui.slidePanel.wasMinimizedBeforeGeometriesList
        ) {
          state.ui.slidePanel.isMinimized = true;
          state.ui.slidePanel.wasMinimizedBeforeGeometriesList = false;
        }

        state.ui.geometriesList.isOpen =
          open !== undefined ? open : !state.ui.geometriesList.isOpen;
      });
    },

    toggleRelatedTasksList: (open?: boolean) => {
      set((state) => {
        state.ui.relatedTasksList.isOpen =
          open !== undefined ? open : !state.ui.relatedTasksList.isOpen;
      });
    },

    setMobilePanelMinimized: (isMinimized: boolean) => {
      set((state) => {
        state.ui.slidePanel.isMinimized = isMinimized;
      });
    },

    setSlidePanelFullscreen: (isFullscreen: boolean) => {
      set((state) => {
        if (state.ui.slidePanel.isFullscreen === isFullscreen) return;
        state.ui.slidePanel.isFullscreen = isFullscreen;
      });
    },

    setPreviewMode: (isPreview: boolean) => {
      set((state) => {
        state.ui.slidePanel.previewMode = isPreview;
      });
    },

    dismissSwipeDownHint: () => {
      set((state) => {
        state.ui.slidePanel.swipeDownHintShown = true;
      });
      storage.set(SWIPE_DOWN_HINT_SEEN_KEY, true);
    },

    dismissSwipeUpHint: () => {
      set((state) => {
        state.ui.slidePanel.swipeUpHintShown = true;
      });
      storage.set(SWIPE_UP_HINT_SEEN_KEY, true);
    },

    toggleToolbarGuide: (open?: boolean) => {
      set((state) => {
        state.ui.toolbarGuide.isOpen =
          open !== undefined ? open : !state.ui.toolbarGuide.isOpen;

        // Reset openedManually flag when closing
        if (!state.ui.toolbarGuide.isOpen) {
          state.ui.toolbarGuide.openedManually = false;
        }
      });
    },

    openToolbarGuideManually: () => {
      set((state) => {
        state.ui.toolbarGuide.isOpen = true;
        state.ui.toolbarGuide.openedManually = true;
      });
    },

    markGuideAsSeen: () => {
      set((state) => {
        state.ui.toolbarGuide.hasSeenGuide = true;
        state.ui.toolbarGuide.isOpen = false;
        state.ui.toolbarGuide.openedManually = false; // Reset manual flag
      });
    },

    setPanelWidth: (width: number) => {
      set((state) => {
        if (state.ui.panelWidth === width) return;
        state.ui.panelWidth = width;
      });
    },

    setIsAnyPanelResizing: (isResizing: boolean) => {
      set((state) => {
        state.ui.isAnyPanelResizing = isResizing;
      });
    },

    toggleLayersPanel: () => {
      const store = get() as StoreWithCrossSlice;
      const willOpen = !store.ui.mapControls.isLayersExpanded;

      // Closing is unconditional — no other panel is being revealed.
      if (!willOpen) {
        set((state) => {
          state.ui.mapControls.isLayersExpanded = false;
        });
        return;
      }

      // Opening enforces the single right-panel rule: guard-close the share
      // config form (prompts when dirty) and the manager, then reveal layers
      // and close the drawing-tools panels.
      withShareFormGuard(store, () => {
        set((state) => {
          state.ui.mapControls.isLayersExpanded = true;
          state.ui.sharePanel.isOpen = false;
        });
        // MUTUAL EXCLUSION: close drawing-tools panels when layers opens.
        store.closeAllToolPanels?.();
      });
    },

    closeLayersPanel: () => {
      set((state) => {
        state.ui.mapControls.isLayersExpanded = false;
      });
    },

    // Share manager is a right-hand SlidePanel — mutually exclusive with the
    // artifact panels. Opening it mirrors an artifact open: close every
    // competing panel first (run outside `set()` since each artifact close
    // produces its own draft), then reveal the share panel.
    openSharePanel: () => {
      const store = get() as StoreWithCrossSlice;
      closeCompetingArtifacts([store.tasks, store.notes, store.obstructions]);

      set((state) => {
        state.ui.mapControls.isLayersExpanded = false;
        state.ui.sharePanel.isOpen = true;
      });
    },

    // Closes only the manager list. The config form is NOT touched here: it
    // owns a guarded close (X / Cancel / Escape → `requestCloseForm`), and the
    // manager's own Escape listener stays active while hidden behind the form —
    // hard-closing the form here would bypass that guard on a dirty form.
    closeSharePanel: () => {
      set((state) => {
        state.ui.sharePanel.isOpen = false;
      });
    },

    // Toolbar share button toggle — mirrors `toggleLayersPanel`. Closing routes
    // through the guard so a dirty config form prompts; opening reveals the
    // manager (which closes competing panels via `openSharePanel`).
    toggleSharePanel: () => {
      const store = get() as StoreWithCrossSlice;
      const isOpen = store.ui.sharePanel.isOpen || store.shareForm.formIsOpen;

      if (!isOpen) {
        store.ui.openSharePanel();
        return;
      }

      withShareFormGuard(store, () => {
        set((state) => {
          state.ui.sharePanel.isOpen = false;
        });
      });
    },

    dismissToolbarScrollHint: () => {
      set((state) => {
        state.ui.toolbarScrollHint.hasSeenScrollHint = true;
      });
    },

    closeAll: () => {
      // Close artifact panels through their slices so AUTO-* side effects fire
      const store = get() as StoreWithCrossSlice;
      store.tasks.closeForm();
      store.tasks.closeDetails();
      store.notes.closeForm();
      store.notes.closeDetails();
      store.obstructions.closeForm();
      store.obstructions.closeDetails();
      store.shareForm.closeForm();

      set((state) => {
        state.ui.sidebar.isOpen = false;
        state.ui.sidePanel.isOpen = false;
        state.ui.geometriesList.isOpen = false;
        state.ui.relatedTasksList.isOpen = false;
        state.ui.slidePanel.isMinimized = false; // Reset mobile panel to expanded state
        state.ui.toolbarGuide.isOpen = false; // Close toolbar guide as well
        state.ui.toolbarGuide.openedManually = false; // Reset manual flag
        state.ui.mapControls.isLayersExpanded = false; // Close layer panel as well
        state.ui.sharePanel.isOpen = false; // Close share manager panel as well
        state.ui.contextMenu.isVisible = false; // Close context menu as well
        state.ui.hoveredMarkerPreview = null; // Dismiss any on-map hover preview as well
        state.ui.hoveredGeometryPreview = null;
        state.ui.mapOverlay.isOpen = false; // Close map overlay as well
        state.ui.isSelectingTaskLocation = false; // Cancel task location selection
        state.ui.isSelectingNoteLocation = false; // Cancel note location selection
        state.ui.isSelectingObstructionLocation = false; // Cancel obstruction location selection
        // Close layer configurator
        state.ui.layerConfigurator.isOpen = false;
        state.ui.layerConfigurator.editingLayerId = null;

        // Clear task selection when closing all panels
        // This ensures consistent state when user closes everything
        state.tasks.selectedId = null;
      });

      // Restore artifact visibility if auto-hidden by a form
      setHideOtherMarkers(get, false);

      // Clear form conflict state
      const fc = (get() as StoreWithCrossSlice).formConflict;
      if (fc?.showDialog) {
        fc.cancelSwitch();
      }
    },

    // ===== CONTEXT MENU ACTIONS =====
    // Unified context menu management (single source of truth)

    showContextMenu: (
      type: ContextMenuType,
      position: { x: number; y: number },
      data: GeometryContextMenuData | TaskContextMenuData | MapContextMenuData
    ) => {
      set((state) => {
        // Auto-close: Only one menu visible at a time (key fix for mobile bug)
        state.ui.contextMenu = {
          isVisible: true,
          type,
          position,
          // Clear all type-specific data first
          geometryData: null,
          taskData: null,
          mapData: null,
        };

        // Set data for the specific menu type
        if (type === 'geometry') {
          state.ui.contextMenu.geometryData = data as GeometryContextMenuData;
        } else if (type === 'task') {
          state.ui.contextMenu.taskData = data as TaskContextMenuData;
        } else if (type === 'map') {
          state.ui.contextMenu.mapData = data as MapContextMenuData;
        }
      });
    },

    hideContextMenu: () => {
      set((state) => {
        state.ui.contextMenu.isVisible = false;
        state.ui.contextMenu.type = null;
        state.ui.contextMenu.geometryData = null;
        state.ui.contextMenu.taskData = null;
        state.ui.contextMenu.mapData = null;
      });
    },

    suppressInteraction: (durationMs = 300) => {
      set((state) => {
        state.ui.interactionSuppressedUntil = Date.now() + durationMs;
      });
    },

    setTaskViewMode: (mode: ViewMode) => {
      set((state) => {
        state.ui.taskViewMode = mode;
      });
    },

    openMapOverlay: () => {
      set((state) => {
        state.ui.mapOverlay.isOpen = true;
      });
    },

    closeMapOverlay: () => {
      set((state) => {
        resetPlacingFlags(state);
      });
    },

    setDashboardDateRange: (range: DateRange) => {
      set((state) => {
        state.ui.dashboard.dateRange = range;
      });
    },

    setNoteViewMode: (mode: NoteViewMode) => {
      set((state) => {
        state.ui.noteViewMode = mode;
      });
    },

    setObstructionViewMode: (mode: ViewMode) => {
      set((state) => {
        state.ui.obstructionViewMode = mode;
      });
    },

    startTaskLocationSelection: () => {
      set((state) => {
        resetPlacingFlags(state);
        state.ui.isSelectingTaskLocation = true;
        state.ui.mapOverlay.isOpen = true;
      });
    },

    cancelTaskLocationSelection: () => {
      set((state) => {
        resetPlacingFlags(state);
      });
    },

    startNoteLocationSelection: () => {
      set((state) => {
        resetPlacingFlags(state);
        state.ui.isSelectingNoteLocation = true;
      });
    },

    cancelNoteLocationSelection: () => {
      set((state) => {
        state.ui.isSelectingNoteLocation = false;
      });
    },

    startObstructionLocationSelection: () => {
      set((state) => {
        resetPlacingFlags(state);
        state.ui.isSelectingObstructionLocation = true;
      });
    },

    cancelObstructionLocationSelection: () => {
      set((state) => {
        state.ui.isSelectingObstructionLocation = false;
      });
    },

    openLayerConfigurator: () => {
      const store = get() as StoreWithCrossSlice;
      withShareFormGuard(store, () => {
        set((state) => {
          state.ui.layerConfigurator.isOpen = true;
          state.ui.layerConfigurator.editingLayerId = null;
          state.ui.sharePanel.isOpen = false; // single right-panel rule
        });
      });
    },

    openLayerConfiguratorForEdit: (layerId: string) => {
      const store = get() as StoreWithCrossSlice;
      withShareFormGuard(store, () => {
        set((state) => {
          state.ui.layerConfigurator.isOpen = true;
          state.ui.layerConfigurator.editingLayerId = layerId;
          state.ui.sharePanel.isOpen = false; // single right-panel rule
        });
      });
    },

    closeLayerConfigurator: () => {
      set((state) => {
        state.ui.layerConfigurator.isOpen = false;
        state.ui.layerConfigurator.editingLayerId = null;
      });
    },

    registerSlidePanel: () => {
      set((state) => {
        state.ui.openSlidePanelCount++;
      });
    },

    unregisterSlidePanel: () => {
      set((state) => {
        state.ui.openSlidePanelCount = Math.max(
          0,
          state.ui.openSlidePanelCount - 1
        );
        // When the last SlidePanel closes, reset the global mobile state so
        // the next panel (e.g. LayersPanel with allowMinimize=false) isn't
        // affected by stale isMinimized/previewMode left over from a prior
        // artifact panel whose own close handler didn't reset them.
        if (state.ui.openSlidePanelCount === 0) {
          state.ui.slidePanel.isMinimized = false;
          state.ui.slidePanel.previewMode = false;
        }
      });
    },

    // Skip allocating an Immer draft when the value is unchanged — mousemove
    // events fire per pointer pixel, so dedup at the call boundary saves a
    // producer + listener notification per frame.
    setHoveredMarkerPreview: (preview: HoveredMarkerPreview | null) => {
      const current = (get() as StoreWithCrossSlice).ui.hoveredMarkerPreview;
      if (areHoverPreviewsEqual(current, preview)) return;
      set((state) => {
        state.ui.hoveredMarkerPreview = preview;
      });
    },

    setHoveredGeometryPreview: (preview: HoveredGeometryPreview | null) => {
      const current = (get() as StoreWithCrossSlice).ui.hoveredGeometryPreview;
      if (areHoverPreviewsEqual(current, preview)) return;
      set((state) => {
        state.ui.hoveredGeometryPreview = preview;
      });
    },
  },
});
